import type { ModuleId, Mutation } from '@tahi/core';
import { makeMutation } from '../mutation';
import {
  getModuleState,
  listModuleStates,
  moduleStateId,
  type ModuleStateRow,
} from '../repos/moduleStates';
import { runCommand, type CommandEnv } from './dispatch';
import { defaultTileMutationsFor } from './tiles';

/** Turning a module off only hides it (spec principle 3); its tiles stay where they were. */
export async function setModuleEnabled(
  env: CommandEnv,
  moduleId: ModuleId,
  enabled: boolean,
): Promise<void> {
  const id = moduleStateId(env.userId, moduleId);
  const existing = await getModuleState(env.db, id);
  const mutations: Mutation[] = [
    existing
      ? makeMutation('module_states', 'update', id, { enabled }, 'system')
      : makeMutation(
          'module_states',
          'insert',
          id,
          { moduleId, enabled, plan: 'free', settings: {} },
          'system',
        ),
  ];
  if (enabled && !existing) mutations.push(...(await defaultTileMutationsFor(env, moduleId)));
  await runCommand(env, { mutations });
}

const settingsOf = (row: ModuleStateRow): Record<string, unknown> =>
  row.settings && typeof row.settings === 'object'
    ? { ...(row.settings as Record<string, unknown>) }
    : {};

/** Bottom-bar pins (conventions §15): navPin 1..3 in `ids` order, -1 for every other module row. */
export async function setNavPins(env: CommandEnv, ids: ModuleId[]): Promise<void> {
  if (ids.length > 3) throw new Error('Pin at most three modules');
  const mutations: Mutation[] = [];
  for (const row of await listModuleStates(env.db)) {
    const settings = settingsOf(row);
    const navPin = ids.indexOf(row.moduleId as ModuleId) + 1 || -1;
    if (settings.navPin === navPin) continue;
    mutations.push(
      makeMutation(
        'module_states',
        'update',
        row.id,
        { settings: { ...settings, navPin } },
        'system',
      ),
    );
  }
  await runCommand(env, { mutations });
}

/** Back to "the first three modules turned on". */
export async function resetNavPins(env: CommandEnv): Promise<void> {
  const mutations: Mutation[] = [];
  for (const row of await listModuleStates(env.db)) {
    const settings = settingsOf(row);
    if (!('navPin' in settings)) continue;
    delete settings.navPin;
    mutations.push(makeMutation('module_states', 'update', row.id, { settings }, 'system'));
  }
  await runCommand(env, { mutations });
}
